/**
 * @file GeneratedContentEditor.tsx
 * @description Component for editing the generated SEO content.
 * Provides an interface to modify title, meta description, and main content.
 * Uses Tiptap for rich text editing capabilities.
 */

import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import { Markdown } from "tiptap-markdown";

import { useContentHeight } from "../../hooks/useContentHeight";
import GeneratedContentWrapper from "./GeneratedContentWrapper";

import "../../styles/GeneratedContentEditor.css";

interface GeneratedContentEditorProps {
  title: string;
  metaDescription: string;
  content: string;
  onChange: (data: {
    title: string;
    metaDescription: string;
    content: string;
  }) => void;
  onSave: () => void;
  onCancel: () => void;
}

const GeneratedContentEditor: React.FC<GeneratedContentEditorProps> = ({
  title,
  metaDescription,
  content,
  onChange,
  onSave,
  onCancel,
}) => {
  const titleProps = useContentHeight(
    title,
    "text-6xl font-semibold leading-tight mb-[.33em]"
  );
  const metaDescriptionProps = useContentHeight(
    metaDescription,
    "text-4xl leading-tight mb-[2.5em]"
  );

  const editor = useEditor({
    extensions: [
      StarterKit,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Markdown.configure({
        html: false,
        transformPastedText: true,
        transformCopiedText: true,
      }),
    ],
    content: content,
    onUpdate: ({ editor }) => {
      const markdown = editor.storage.markdown.getMarkdown();
      onChange({ title, metaDescription, content: markdown });
    },
  });

  const handleTitleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange({ title: e.target.value, metaDescription, content });
  };

  const handleMetaDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    onChange({ title, metaDescription: e.target.value, content });
  };

  const toolbar = (
    <>
      <button
        onClick={onCancel}
        className="px-6 text-base rounded-full btn btn-outline btn-primary"
      >
        Annulla
      </button>
      <button
        onClick={onSave}
        className="px-6 text-base rounded-full btn btn-primary"
      >
        Salva
      </button>
    </>
  );

  return (
    <GeneratedContentWrapper toolbar={toolbar}>
      <div className="relative flex">
        <div
          ref={titleProps.measureRef}
          className={`${titleProps.className} absolute whitespace-pre-wrap invisible`}
        >
          {title}
        </div>
        <textarea
          value={title}
          onChange={handleTitleChange}
          className={`${titleProps.className} w-full resize-none overflow-hidden`}
          style={{ height: titleProps.height }}
        />
      </div>
      <div className="relative flex">
        <div
          ref={metaDescriptionProps.measureRef}
          className={`${metaDescriptionProps.className} absolute whitespace-pre-wrap invisible`}
        >
          {metaDescription}
        </div>
        <textarea
          value={metaDescription}
          onChange={handleMetaDescriptionChange}
          className={`${metaDescriptionProps.className} w-full resize-none overflow-hidden`}
          style={{ height: metaDescriptionProps.height }}
        />
      </div>
      <EditorContent editor={editor} />
    </GeneratedContentWrapper>
  );
};

export default GeneratedContentEditor;
